import React, { useEffect, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { kapsoAPI, messagingAPI, whatsappInboxAPI, openInboxStream } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useRecipients, RecipientPicker, RecipientCount } from '../components/messaging/RecipientPicker';
import { PLACEHOLDERS, CATEGORY_LABELS, SERVICE_TYPE_LABELS } from './MessageCenterPage';
import { useWidth, Bubble, ChatRow, CustomerStrip, CustomerCard, WorkflowBox, fmtDay, isSameDay } from './WhatsAppInboxPage';
import {
  BadgeCheck, RefreshCw, Loader, Send, MessagesSquare, LayoutTemplate, Megaphone, Users,
  Building2, ListChecks, Trash2, Plus, X, ShieldBan, CheckCircle2, XCircle, AlertTriangle, Search, ArrowLeft, Info, Bot,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import MessageLogPanel from '../components/messaging/MessageLogPanel';
import WaSwitch from '../components/messaging/WaSwitch';

// Kapso = official WhatsApp Cloud API (kapso.ai). Every send on this page is manual;
// nothing automated routes through Kapso yet. Backend: routes/kapso.js.

const errMsg = (err, fallback) => err.response?.data?.message || fallback;
const label = { fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 };
const muted = { fontSize: '.8rem', color: 'var(--text-muted)' };
const spin = { animation: 'spin 1s linear infinite' };
const varsIn = (text) => [...new Set([...(text || '').matchAll(/\{\{\s*([a-z0-9_]+)\s*\}\}/g)].map(m => m[1]))];
const fmtTs = (t) => {
  if (!t) return '';
  const d = /^\d+$/.test(String(t)) ? new Date(Number(t) * 1000) : new Date(t);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function Pill({ text, tone = 'muted' }) {
  const tones = {
    ok:    { color: 'var(--c-0ca30c)', bg: '#0ca30c17' },
    warn:  { color: 'var(--c-92400e)', bg: 'var(--cb-fffbeb)' },
    bad:   { color: 'var(--danger, #dc2626)', bg: '#dc262617' },
    muted: { color: 'var(--text-muted)', bg: 'var(--bg-alt, #f3f4f6)' },
  };
  const t = tones[tone] || tones.muted;
  return <span style={{ fontSize: '.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: t.color, background: t.bg, whiteSpace: 'nowrap' }}>{text}</span>;
}
const statusTone = (s) => (['APPROVED', 'CONNECTED', 'completed', 'read', 'delivered', 'active', 'healthy'].includes(s) ? 'ok'
  : ['REJECTED', 'failed', 'DISABLED', 'unhealthy'].includes(s) ? 'bad' : 'warn');

function Card({ title, right, children }) {
  return (
    <div className="card mb-6">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>{title}</h3>{right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview', icon: BadgeCheck },
  { key: 'customers', label: 'Send to Customers', icon: Users },
  { key: 'inbox', label: 'Inbox', icon: MessagesSquare },
  { key: 'bot', label: 'Chatbot', icon: Bot },
  { key: 'send', label: 'Send to a Number', icon: Send },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'broadcasts', label: 'Broadcasts', icon: Megaphone },
  { key: 'contacts', label: 'Contacts', icon: Users },
  { key: 'profile', label: 'Profile & Blocking', icon: Building2 },
  { key: 'log', label: 'Message Log', icon: ListChecks },
  { key: 'features', label: 'All Features', icon: ListChecks },
];

// Phone-only grouping of the ten tabs into four screens (the full tab row stays on desktop; CSS swaps them).
const TAB_GROUPS = [
  { key: 'status', label: 'Status', tabs: ['overview'] },
  { key: 'messages', label: 'Messages', tabs: ['customers', 'send', 'inbox', 'log', 'contacts'] },
  { key: 'tpl', label: 'Templates', tabs: ['templates', 'broadcasts'] },
  { key: 'settings', label: 'Settings', tabs: ['bot', 'profile', 'features'] },
];

export default function KapsoPage() {
  useDarkReady();
  const [tab, setTab] = useState('overview');
  const [numbers, setNumbers] = useState(null);
  const [error, setError] = useState('');
  const [pid, setPid] = useState('');

  const loadNumbers = useCallback(async () => {
    setError('');
    try {
      const res = await kapsoAPI.numbers();
      setNumbers(res.data.data);
      const prod = res.data.data.find(n => n.kind !== 'sandbox') || res.data.data[0];
      setPid(p => p || prod?.phone_number_id || '');
    } catch (err) {
      setError(errMsg(err, 'Could not reach Kapso'));
      setNumbers([]);
    }
  }, []);
  useEffect(() => { loadNumbers(); }, [loadNumbers]);

  const number = (numbers || []).find(n => n.phone_number_id === pid);
  const ctx = { pid, waba: number?.business_account_id, number, numbers: numbers || [] };

  return (
    <div>
      <WaSwitch active="official" numbers={numbers} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <BadgeCheck size={22} color="var(--primary)" />
        <h2 style={{ margin: 0, flex: 1 }}>Official API (Kapso)</h2>
        {numbers?.length > 1 && (
          <select className="form-control" style={{ maxWidth: 280 }} value={pid} onChange={e => setPid(e.target.value)}>
            {numbers.map(n => <option key={n.phone_number_id} value={n.phone_number_id}>{n.display_name || n.name} {n.display_phone_number ? `(${n.display_phone_number})` : '(sandbox)'}</option>)}
          </select>
        )}
      </div>
      <p style={{ ...muted, fontSize: '.85rem', marginTop: 2, marginBottom: 16 }}>
        Official WhatsApp Business API through Kapso. Separate from the Baileys gateway (WhatsApp API page);
        nothing automatic sends from here. Outside 24 hours of a customer's last message, only approved templates are delivered.
      </p>

      <div className="kp-groups">
        <div className="kp-group-row">
          {TAB_GROUPS.map(g => (
            <button key={g.key} type="button" aria-pressed={g.tabs.includes(tab)}
              className={g.tabs.includes(tab) ? 'kp-g kp-g-on' : 'kp-g'} onClick={() => !g.tabs.includes(tab) && setTab(g.tabs[0])}>
              {g.label}
            </button>
          ))}
        </div>
        {(TAB_GROUPS.find(g => g.tabs.includes(tab))?.tabs.length || 0) > 1 && (
          <div className="kp-sub-row">
            {TAB_GROUPS.find(g => g.tabs.includes(tab)).tabs.map(k => {
              const t = TABS.find(x => x.key === k);
              return <button key={k} type="button" className={tab === k ? 'kp-s kp-s-on' : 'kp-s'} onClick={() => setTab(k)}>{t.label}</button>;
            })}
          </div>
        )}
      </div>

      <div className="kp-all" style={{ display: 'flex', gap: 14, marginBottom: 20, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 2px', marginBottom: -1, whiteSpace: 'nowrap',
              background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--primary)' : 'transparent'}`,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600, fontSize: '.85rem', cursor: 'pointer',
            }}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {error ? <div className="card card-body" style={{ color: 'var(--danger, #dc2626)' }}><XCircle size={14} /> {error}</div>
        : numbers === null ? <Loader size={18} style={spin} />
          : tab === 'overview' ? <OverviewTab {...ctx} reload={loadNumbers} />
            : tab === 'customers' ? <CustomersTab {...ctx} />
            : tab === 'inbox' ? <InboxTab {...ctx} goSend={() => setTab('send')} />
            : tab === 'bot' ? <BotTab {...ctx} />
              : tab === 'send' ? <SendTab {...ctx} />
                : tab === 'templates' ? <TemplatesTab {...ctx} />
                  : tab === 'broadcasts' ? <BroadcastsTab {...ctx} />
                    : tab === 'contacts' ? <ContactsTab />
                      : tab === 'log' ? <MessageLogPanel integration="kapso" />
                      : tab === 'profile' ? <ProfileTab {...ctx} />
                        : <FeaturesTab />}
    </div>
  );
}

// ─── Overview: numbers + live health ───
function OverviewTab({ numbers, reload }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => { setBusy(true); await reload(); setBusy(false); };
  return (
    <>
    <AccountsCard onChange={reload} />
    <NumberRoles numbers={numbers} />
    <Card title="Numbers" right={<button className="btn btn-secondary btn-sm" onClick={refresh} disabled={busy}><RefreshCw size={13} style={busy ? spin : {}} /></button>}>
      {!numbers.length && <div style={muted}>No numbers on this Kapso project.</div>}
      <div style={{ display: 'grid', gap: 12 }}>
        {numbers.map(n => {
          const mh = n.health?.checks?.messaging_health;
          const notes = (mh?.details?.entities || []).flatMap(e => e.additional_info || []);
          return (
            <div key={n.phone_number_id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 700 }}>{n.display_name || n.name}</div>
                  <div style={muted}>{n.display_phone_number || 'Sandbox (test only)'} · number ID {n.phone_number_id}</div>
                </div>
                {n.kind === 'sandbox' ? <Pill text="Sandbox" tone="warn" /> : <Pill text={n.status || 'Unknown'} tone={statusTone(n.status)} />}
                {n.health && <Pill text={`Health: ${n.health.status}`} tone={n.health.status === 'healthy' ? 'ok' : 'warn'} />}
              </div>
              {n.kind !== 'sandbox' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginTop: 10, fontSize: '.8rem' }}>
                  <div><span style={muted}>Quality</span><br />{n.quality_rating || '—'}</div>
                  <div><span style={muted}>Daily limit</span><br />{(n.whatsapp_business_manager_messaging_limit || '—').replace('TIER_', '') } customers / 24h</div>
                  <div><span style={muted}>Name status</span><br />{n.name_status || '—'}</div>
                  <div><span style={muted}>Can send</span><br />{mh?.overall_status || '—'}</div>
                  <div><span style={muted}>Business account</span><br />{n.business_account_id}</div>
                </div>
              )}
              {notes.map((t, i) => <div key={i} style={{ ...muted, marginTop: 8, color: 'var(--c-92400e)' }}><AlertTriangle size={13} /> {t}</div>)}
            </div>
          );
        })}
      </div>
    </Card>
    </>
  );
}

// Each service has its own Kapso account. Its API key (and, for the inbox + chatbot, its webhook
// secret) is saved here; the server only ever says whether they are set, never shows them.
function AccountsCard({ onChange }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ catv: {}, broadband: {} });
  const [busy, setBusy] = useState('');
  const load = () => kapsoAPI.accounts().then(r => setState(r.data.data)).catch(() => setState({}));
  useEffect(() => { load(); }, []);
  const set = (acc, k, v) => setForm(f => ({ ...f, [acc]: { ...f[acc], [k]: v } }));
  const save = async (acc) => {
    setBusy(acc);
    try {
      await kapsoAPI.saveAccount(acc, form[acc]);
      setForm(f => ({ ...f, [acc]: {} }));
      toast.success('Saved');
      await load();
      if (onChange) onChange();
    } catch (err) { toast.error(errMsg(err, 'Could not save')); }
    setBusy('');
  };
  const remove = async (acc) => {
    if (!window.confirm('Remove what is saved in the CRM for this account?')) return;
    setBusy(acc);
    try { await kapsoAPI.removeAccount(acc); await load(); if (onChange) onChange(); } catch (err) { toast.error(errMsg(err, 'Could not remove')); }
    setBusy('');
  };
  if (!state) return null;
  const hook = `${window.location.origin}/api/v1/webhooks/kapso`;
  return (
    <Card title="Kapso accounts">
      {[['catv', 'CableTV'], ['broadband', 'Broadband']].map(([acc, name]) => {
        const st = state[acc] || {};
        return (
          <div key={acc} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ minWidth: 150 }}>
              <b>{name}</b>
              <div style={muted}>{st.configured ? `Connected (${st.source === 'crm' ? 'saved here' : 'from server file'})` : 'Not set up'}{st.configured && !st.webhookSecretSet ? ' · no webhook secret' : ''}</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <span style={label}>API key</span>
              <input className="form-control" type="password" autoComplete="off" placeholder={st.configured ? 'Leave empty to keep the current key' : 'Paste the Kapso API key'}
                value={form[acc].apiKey || ''} onChange={e => set(acc, 'apiKey', e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <span style={label}>Webhook secret (optional)</span>
              <input className="form-control" type="password" autoComplete="off" placeholder={st.webhookSecretSet ? 'Leave empty to keep the current secret' : 'Paste the webhook secret'}
                value={form[acc].webhookSecret || ''} onChange={e => set(acc, 'webhookSecret', e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => save(acc)} disabled={busy === acc || (!form[acc].apiKey && !form[acc].webhookSecret)}>
              {busy === acc ? <Loader size={13} style={spin} /> : 'Save'}
            </button>
            {st.source === 'crm' && <button className="btn btn-secondary btn-sm" onClick={() => remove(acc)} disabled={busy === acc}>Remove</button>}
          </div>
        );
      })}
      <div style={muted}>
        The key is checked with Kapso before it is saved. For received messages and the chatbot, add a webhook in each Kapso account that points to <b>{hook}</b> and paste its secret here.
      </div>
    </Card>
  );
}

// Which number messages CableTV customers and which Broadband ones (Send to Customers → "Auto"),
// and a Kapso setup link to connect one more number (Meta sign-up, done by the owner).
function NumberRoles({ numbers }) {
  const [roles, setRoles] = useState(null);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  const [linking, setLinking] = useState(false);
  useEffect(() => { kapsoAPI.numberRoles().then(r => setRoles(r.data.data)).catch(() => setRoles({ catv: '', broadband: '' })); }, []);
  const real = numbers.filter(n => n.kind !== 'sandbox');
  const save = async () => {
    setSaving(true);
    try { setRoles((await kapsoAPI.saveNumberRoles(roles)).data.data); toast.success('Saved'); } catch (err) { toast.error(errMsg(err, 'Could not save')); }
    setSaving(false);
  };
  const addNumber = async (account) => {
    setLinking(true);
    try { setLink((await kapsoAPI.setupLink(account)).data.data); } catch (err) { toast.error(errMsg(err, 'Could not create the setup link')); }
    setLinking(false);
  };
  if (!roles) return null;
  const pick = (key, text) => (
    <div style={{ flex: 1, minWidth: 220 }}>
      <span style={label}>{text}</span>
      <select className="form-control" value={roles[key]} onChange={e => setRoles(r => ({ ...r, [key]: e.target.value }))}>
        <option value="">— Not set —</option>
        {real.map(n => <option key={n.phone_number_id} value={n.phone_number_id}>{n.display_name || n.name} ({n.display_phone_number})</option>)}
      </select>
    </div>
  );
  return (
    <Card title="Number per service" right={
      <span style={{ display: 'flex', gap: 6 }}>
        {[['catv', 'CableTV'], ['broadband', 'Broadband']].map(([acc, name]) => (
          <button key={acc} className="btn btn-secondary btn-sm" onClick={() => addNumber(acc)} disabled={linking}>
            {linking ? <Loader size={13} style={spin} /> : <Plus size={13} />} Add {name} number
          </button>
        ))}
      </span>}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {pick('catv', 'CableTV customers get messages from')}
        {pick('broadband', 'Broadband customers get messages from')}
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? <Loader size={13} style={spin} /> : 'Save'}</button>
      </div>
      <div style={{ ...muted, marginTop: 8 }}>
        Used by Send to Customers → "Auto". Customers with both services follow the list you picked (CableTV or Broadband); otherwise they get the CableTV number.
        Templates must be approved on each number's business account.
      </div>
      {link && (
        <div style={{ marginTop: 10, fontSize: '.85rem' }}>
          <a href={link.url} target="_blank" rel="noopener noreferrer"><b>Open the Kapso sign-up page</b></a> and log in with the Facebook account that owns the business.
          When it says connected, press refresh on Numbers below and choose the new number here. The link works for 30 days.
        </div>
      )}
    </Card>
  );
}

// ─── Inbox: same look and workflow as the WhatsApp Inbox page ───
// Reuses WhatsAppInboxPage's chat row, bubbles, customer strip/card and Handling box (assign,
// resolve, internal notes — stored in WhatsAppChatMeta as 'kapso:<conversation id>' through the
// WhatsApp Inbox's own meta/notes routes). Kept current by the Kapso webhook through
// /kapso/stream; the slow polls are only a backstop for a dropped stream.
const KAPSO_LIST_POLL_MS = 30000;
const KAPSO_CHAT_POLL_MS = 30000;
const metaId = (chat) => `kapso:${chat.chatId}`;

// Kapso conversation / message → the shapes ChatRow and Bubble expect.
const toChat = (c) => ({
  chatId: c.id, phone: String(c.phone_number || '').replace(/\D/g, ''), name: c.kapso?.contact_name || '',
  lastMessageAt: c.kapso?.last_message_timestamp || c.updated_at, lastMessageText: c.kapso?.last_message_text || '',
  unreadCount: c.unreadCount || 0, customers: c.customers || [], isGroup: false,
  assignedTo: c.assignedTo, assignedToName: c.assignedToName, chatStatus: c.chatStatus, botPausedUntil: c.botPausedUntil,
});
const toMsg = (m) => ({
  waId: m.id, direction: m.kapso?.direction === 'outbound' ? 'out' : 'in', type: 'text',
  text: m.kapso?.content || m.text?.body || `[${m.type}]`,
  timestamp: /^\d+$/.test(String(m.timestamp)) ? Number(m.timestamp) * 1000 : m.timestamp, status: m.kapso?.status,
});

function InboxTab({ pid, goSend }) {
  const { user } = useAuth();
  const width = useWidth();
  const wide = width >= 1200;
  const narrow = width < 860;
  const [chats, setChats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [live, setLive] = useState(false);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [staff, setStaff] = useState([]);
  const [showSide, setShowSide] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const loadChats = useCallback(async () => {
    try { setChats((await kapsoAPI.conversations({ phone_number_id: pid })).data.data.map(toChat)); }
    catch (err) { toast.error(errMsg(err, 'Could not load chats')); setChats(c => c || []); }
  }, [pid]);
  const loadMessages = useCallback(async (chat) => {
    try { setMessages([...(await kapsoAPI.messages(chat.chatId)).data.data].reverse().map(toMsg)); }
    catch (err) { toast.error(errMsg(err, 'Could not load messages')); }
  }, []);

  useEffect(() => {
    loadChats();
    whatsappInboxAPI.getStaff().then(r => setStaff(r.data.data || [])).catch(() => {});
    const t = setInterval(loadChats, KAPSO_LIST_POLL_MS);
    // Webhook → /kapso/stream: refresh the list, and the open chat if the event is for it.
    const stop = openInboxStream({
      path: '/kapso/stream',
      onOpen: () => setLive(true),
      onError: () => setLive(false),
      onEvent: (e) => {
        loadChats();
        const cur = selectedRef.current;
        if (cur && e.conversationId === cur.chatId) {
          loadMessages(cur);
          if (e.direction === 'inbound') kapsoAPI.markRead(cur.chatId).catch(() => {});
        }
      },
    });
    return () => { clearInterval(t); stop(); };
  }, [loadChats, loadMessages]);

  useEffect(() => {
    if (!selected) return undefined;
    setMessages([]);
    setMeta(null);
    loadMessages(selected);
    setCustomersLoading(true);
    kapsoAPI.customers(selected.phone).then(r => setCustomers(r.data.data || [])).catch(() => setCustomers([])).finally(() => setCustomersLoading(false));
    whatsappInboxAPI.getMeta(metaId(selected)).then(r => setMeta(r.data.data)).catch(() => {});
    if (selected.unreadCount) kapsoAPI.markRead(selected.chatId).then(loadChats).catch(() => {});
    const t = setInterval(() => loadMessages(selected), KAPSO_CHAT_POLL_MS);
    return () => clearInterval(t);
    // Only when a different chat is opened, not when the list refresh hands back a new object.
    // eslint-disable-next-line
  }, [selected?.chatId]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages.length]);

  const saveMeta = async (patch) => {
    try { setMeta((await whatsappInboxAPI.saveMeta(metaId(selected), patch)).data.data); loadChats(); }
    catch (err) { toast.error(errMsg(err, 'Could not update')); }
  };
  const addNote = async (noteText) => {
    try { setMeta((await whatsappInboxAPI.addNote(metaId(selected), noteText)).data.data); loadChats(); }
    catch (err) { toast.error(errMsg(err, 'Could not add the note')); }
  };

  const lastIn = [...messages].reverse().find(m => m.direction === 'in');
  const windowOpen = !!lastIn && Date.now() - new Date(lastIn.timestamp).getTime() < 24 * 3600 * 1000;

  const send = async () => {
    if (!text.trim() || !windowOpen) return;
    setSending(true);
    try {
      // conversationId: a staff reply pauses the chatbot on this chat (backend kapso.js#send).
      await kapsoAPI.send({ phoneNumberId: pid, to: selected.phone, kind: 'text', message: text.trim(), conversationId: selected.chatId });
      setText('');
      loadMessages(selected);
      loadChats();
    } catch (err) { toast.error(errMsg(err, 'Send failed')); }
    setSending(false);
  };
  const onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const botPaused = (chats || []).find(c => c.chatId === selected?.chatId)?.botPausedUntil;
  const resumeBot = async () => {
    try { await kapsoAPI.botResume(selected.chatId); toast.success('Chatbot will answer this customer again'); loadChats(); }
    catch (err) { toast.error(errMsg(err, 'Could not resume the chatbot')); }
  };

  const q = search.trim().toLowerCase();
  const shownChats = (chats || []).filter(c =>
    (!q || [c.name, c.phone, c.lastMessageText, ...c.customers.map(x => x.name)].some(v => String(v || '').toLowerCase().includes(q)))
    && (!statusFilter || (statusFilter === 'resolved' ? c.chatStatus === 'resolved' : c.chatStatus !== 'resolved'))
    && (!assignedFilter || (assignedFilter === 'me' ? String(c.assignedTo) === String(user?._id) : !c.assignedTo))
    && (!unreadOnly || c.unreadCount > 0));
  const nameOf = (c) => c.customers?.[0]?.name || c.name || `+${c.phone}`;
  const selectStyle = { flex: 1, padding: '5px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, minWidth: 0 };

  const listPane = (
    <div style={{ width: narrow ? '100%' : 340, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg-card)', minHeight: 0 }}>
      <div style={{ padding: 10, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted)' }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number or message"
              style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
          </div>
          <button className="btn btn-secondary" onClick={loadChats} title="Refresh" aria-label="Refresh"><RefreshCw size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="">All</option>
          </select>
          <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)} style={selectStyle}>
            <option value="">Anyone</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> Unread only
          <span style={{ marginLeft: 'auto', fontSize: 11, color: live ? 'var(--c-0ca30c)' : 'var(--text-muted)' }} title={live ? 'Live updates connected' : 'Reconnecting — updates may lag'}>
            {live ? '● Live' : '○ Reconnecting'}
          </span>
        </label>
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {chats === null && <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
        {chats && shownChats.length === 0 && (
          <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
            {q || unreadOnly || assignedFilter || statusFilter ? 'No chats match these filters.' : 'No conversations yet. Chats appear here once someone messages your Kapso number.'}
          </div>
        )}
        {shownChats.map(c => (
          <ChatRow key={c.chatId} chat={c} active={c.chatId === selected?.chatId} onClick={() => { setSelected(c); setShowInfo(false); }} />
        ))}
      </div>
    </div>
  );

  const infoPanel = selected && (
    <div style={{ padding: 12, overflowY: 'auto', height: '100%' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{nameOf(selected)}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>+{selected.phone}</div>
      <WorkflowBox meta={meta} staff={staff} canReply onSaveMeta={saveMeta} onAddNote={addNote} />
      {customersLoading && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading customer…</div>}
      {!customersLoading && customers.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No CRM customer has this number.</div>}
      {customers.map(c => <CustomerCard key={c._id} c={c} />)}
    </div>
  );

  const conversationPane = selected ? (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: 'var(--cb-efeae2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        {narrow && <button className="btn btn-secondary" onClick={() => setSelected(null)} aria-label="Back"><ArrowLeft size={16} /></button>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(selected)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            +{selected.phone}{selected.name && selected.customers?.[0]?.name && selected.name !== selected.customers[0].name ? ` · WhatsApp: ${selected.name}` : ''}
            {meta?.assignedToName && ` · ${meta.assignedToName}`}
            {meta?.status === 'resolved' && ' · Resolved'}
          </div>
        </div>
        <button className="btn btn-secondary" aria-pressed={wide ? showSide : showInfo} onClick={() => (wide ? setShowSide(v => !v) : setShowInfo(v => !v))}>
          <Info size={16} /> Customer
        </button>
      </div>

      {!(showInfo && !wide) && <CustomerStrip customers={customers} loading={customersLoading} onMore={() => (wide ? setShowSide(true) : setShowInfo(true))} />}

      {showInfo && !wide ? (
        <div style={{ flex: 1, minHeight: 0, background: 'var(--bg)' }}>{infoPanel}</div>
      ) : (
        <>
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', minHeight: 0 }}>
            {messages.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 20 }}>Loading messages…</div>}
            {messages.map((m, i) => (
              <React.Fragment key={m.waId}>
                {(i === 0 || !isSameDay(messages[i - 1].timestamp, m.timestamp)) && (
                  <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <span style={{ background: 'var(--cb-e1f3fb)', fontSize: 12, padding: '3px 10px', borderRadius: 8 }}>{fmtDay(m.timestamp)}</span>
                  </div>
                )}
                <Bubble msg={m} showSender={false} />
              </React.Fragment>
            ))}
          </div>

          <div style={{ padding: 10, background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
            {botPaused && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                🤖 Chatbot paused for this customer until {fmtTs(botPaused)} (staff are handling it).
                <button className="btn btn-secondary btn-sm" onClick={resumeBot}>Hand back to chatbot</button>
              </div>
            )}
            {!windowOpen && messages.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--c-92400e)', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <AlertTriangle size={13} /> More than 24 hours since their last message: WhatsApp only allows an approved template now.
                <button className="btn btn-secondary btn-sm" onClick={goSend}>Send a template</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} rows={1} disabled={!windowOpen}
                placeholder={windowOpen ? 'Type a message — Enter to send, Shift+Enter for a new line' : 'Reply window closed'}
                style={{ flex: 1, resize: 'none', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, maxHeight: 120, fontFamily: 'inherit' }} />
              <button className="btn btn-primary" onClick={send} disabled={sending || !windowOpen || !text.trim()}><Send size={16} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  ) : (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-muted)', background: 'var(--bg)' }}>Select a conversation</div>
  );

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 230px)', minHeight: 480, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-card)' }}>
      {(!narrow || !selected) && listPane}
      {(!narrow || selected) && conversationPane}
      {wide && selected && showSide && (
        <div style={{ width: 320, borderLeft: '1px solid var(--border)', background: 'var(--bg)', minHeight: 0 }}>{infoPanel}</div>
      )}
    </div>
  );
}

// Fetches APPROVED templates once for the Send and Broadcast tabs.
function useApprovedTemplates(waba) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!waba) return;
    kapsoAPI.templates(waba).then(r => setList(r.data.data.filter(t => t.status === 'APPROVED'))).catch(() => setList([]));
  }, [waba]);
  return list;
}
// Image picker for template image headers: upload (stored with Message Center's template
// images, served publicly so Kapso/Meta can fetch it) or paste a public link.
function ImagePick({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5 MB (Meta limit)');
    setBusy(true);
    try { onChange((await messagingAPI.uploadTemplateImage(file)).data.data.url); }
    catch (err) { toast.error(errMsg(err, 'Upload failed')); }
    setBusy(false);
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="file" accept="image/jpeg,image/png" onChange={e => upload(e.target.files[0])} disabled={busy} />
        {busy && <Loader size={13} style={spin} />}
      </div>
      <input className="form-control" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="…or paste a public image link (https://…)" />
      {value && <img src={value} alt="" style={{ maxWidth: 220, maxHeight: 160, borderRadius: 8, border: '1px solid var(--border)' }} />}
    </div>
  );
}

const tplParts = (tpl) => {
  const comp = (type) => (tpl?.components || []).find(c => c.type === type);
  const header = comp('HEADER');
  return { header, body: comp('BODY'), bodyVars: varsIn(comp('BODY')?.text), headerVars: header?.format === 'TEXT' ? varsIn(header.text) : [] };
};

// ─── Chatbot: on/off + greeting (backend services/kapsoBot.js) ───
function BotTab({ number }) {
  const [bot, setBot] = useState(null);
  const [greeting, setGreeting] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    kapsoAPI.bot().then(r => { setBot(r.data.data); setGreeting(r.data.data.greeting); }).catch(err => toast.error(errMsg(err, 'Could not load chatbot')));
  }, []);
  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await kapsoAPI.saveBot(patch);
      setBot(b => ({ ...b, ...r.data.data }));
      toast.success(patch.enabled === undefined ? 'Saved' : patch.enabled ? 'Chatbot is ON' : 'Chatbot is OFF');
    } catch (err) { toast.error(errMsg(err, 'Could not save')); }
    setSaving(false);
  };
  if (!bot) return <Loader size={16} style={spin} />;

  return (
    <>
      <Card title="Chatbot" right={<Pill text={bot.enabled ? 'ON' : 'OFF'} tone={bot.enabled ? 'ok' : 'muted'} />}>
        <div style={{ display: 'grid', gap: 12, maxWidth: 620 }}>
          <div style={{ fontSize: '.85rem' }}>
            When ON, every message to <b>{number?.display_phone_number || 'the Kapso number'}</b> gets an instant reply with a tap menu.
            Answers come from the CRM using the customer's mobile number. Replies are free: they go inside the 24-hour window the customer opened.
          </div>
          <div>
            <button className={`btn btn-sm ${bot.enabled ? 'btn-secondary' : 'btn-primary'}`} disabled={saving}
              onClick={() => (bot.enabled || window.confirm('Turn the chatbot ON? It will start replying to customers on the Kapso number right away.')) && save({ enabled: !bot.enabled })}>
              {saving ? <Loader size={13} style={spin} /> : <Bot size={13} />} {bot.enabled ? 'Turn OFF' : 'Turn ON'}
            </button>
          </div>
          <div>
            <label style={label}>Greeting (shown above the menu)</label>
            <textarea className="form-control" rows={3} value={greeting} onChange={e => setGreeting(e.target.value)} maxLength={900} />
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} disabled={saving || !greeting.trim() || greeting === bot.greeting} onClick={() => save({ greeting })}>Save greeting</button>
          </div>
        </div>
      </Card>
      <Card title="What the customer sees">
        <div style={{ display: 'grid', gap: 6, fontSize: '.85rem' }}>
          {bot.menu.map((m, i) => (
            <div key={m.id}><b>{i + 1}. {m.title}</b> <span style={muted}>— {{
              due: 'their balance for every account on that mobile number, with a "Pay now" button',
              expiry: 'TV and internet expiry dates with plan names',
              pay: 'their online payment link',
              complaint: '"Internet not working" / "TV not working" buttons → creates a complaint ticket (staff get the usual new-complaint alert; no duplicate if one is already open)',
              staff: `the chatbot goes quiet for ${bot.pauseHours} hours and the chat shows as unread in the Inbox`,
            }[m.id]}</span></div>
          ))}
          <div style={{ ...muted, marginTop: 6 }}>
            Customers can tap the menu or type 1–5. When staff reply from the Inbox, the chatbot also pauses for {bot.pauseHours} hours on that chat
            ("Hand back to chatbot" in the chat undoes it). Numbers not in the CRM only get a "Talk to staff" button.
          </div>
        </div>
      </Card>
    </>
  );
}

// ─── Send to Customers: Message Center → Send, through Kapso ───
// Same screen as Message Center → Send: pick one of Message Center's templates (or write a
// message), pick recipients, send. Meta only delivers a business-started message if its
// wording is an APPROVED template, so each Message Center template has an approved copy on
// Kapso (same name in lowercase_with_underscores, submitted from here with one click):
//   approved copy + wording unchanged → sent as that template, reaches everyone;
//   otherwise → sent as typed, reaches only people who messaged in the last 24 hours.
// Backend: kapso.js#send-bulk.
const kapsoNameFor = (t) => snake(String(t?.name || '')).replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
const MARKETING_CATEGORIES = ['plans', 'win_back', 'custom', 'lead_interest'];

function CustomersTab({ pid, waba, number }) {
  const rcp = useRecipients();
  const { sendAudience, recipientCount } = rcp;
  const [templates, setTemplates] = useState([]); // Message Center templates
  const [kTemplates, setKTemplates] = useState([]); // every Kapso template, any status
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState(''); // for an approved copy with an image header
  const [usage, setUsage] = useState(null);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [roles, setRoles] = useState(null); // { catv, broadband } for the "Auto" sender
  const [from, setFrom] = useState('selected'); // 'auto' | 'selected'
  const sandbox = number?.kind === 'sandbox';
  useEffect(() => {
    kapsoAPI.numberRoles().then(r => {
      setRoles(r.data.data);
      if (r.data.data.catv && r.data.data.broadband) setFrom('auto');
    }).catch(() => {});
  }, []);
  const auto = from === 'auto';
  const sendPid = auto ? 'auto' : pid;
  const fromText = auto ? 'the CableTV / Broadband numbers (by service)' : `${number?.display_name} (${number?.display_phone_number})`;

  useEffect(() => {
    messagingAPI.getTemplates().then(r => setTemplates((r.data?.data || []).filter(t => t.channel !== 'sms' && t.isActive !== false))).catch(() => {});
  }, []);
  const loadKapso = useCallback(() => {
    if (!waba || sandbox) return;
    kapsoAPI.templates(waba).then(r => setKTemplates(r.data.data || [])).catch(() => setKTemplates([]));
    kapsoAPI.usage(pid).then(r => setUsage(r.data.data)).catch(() => setUsage(null));
  }, [waba, pid, sandbox]);
  useEffect(() => { loadKapso(); }, [loadKapso]);

  const mc = templates.find(t => t._id === templateId);
  // Approved Kapso templates with no Message Center twin (made in Kapso directly) are picked as 'k:<id>'.
  const mcNames = new Set(templates.map(kapsoNameFor));
  const kOnly = kTemplates.filter(k => k.status === 'APPROVED' && !mcNames.has(k.name));
  const ko = kOnly.find(k => `k:${k.id}` === templateId);
  const kName = kapsoNameFor(mc);
  const copies = kTemplates.filter(k => k.name === kName);
  const copy = ko || copies.find(k => k.status === 'APPROVED') || copies[0];
  const approved = copy?.status === 'APPROVED';
  const baseBody = ko ? (tplParts(ko).body?.text || '') : mc?.body;
  const useTemplate = (!!mc || !!ko) && approved && body === baseBody;
  const needsImage = useTemplate && tplParts(copy).header?.format === 'IMAGE';
  const draft = mc ? fromMessageCenter(mc) : null;
  const tooLong = draft && String(mc.body || '').length > 1024;
  const edgeBlank = draft && /^\s*\{\{|\}\}\s*$/.test(String(mc.body || ''));
  const left = usage && usage.limit != null ? Math.max(0, usage.limit - usage.used) : null;

  const applyTemplate = (id) => {
    setTemplateId(id);
    const k = kOnly.find(x => `k:${x.id}` === id);
    const t = templates.find(x => x._id === id);
    if (k) setBody(tplParts(k).body?.text || '');
    else if (t) setBody(t.body);
    setImage(t?.imageUrl || '');
  };

  const submitToMeta = async () => {
    setSubmitting(true);
    try {
      await kapsoAPI.createTemplate(waba, { ...draft, name: kName, category: MARKETING_CATEGORIES.includes(mc.category) ? 'MARKETING' : 'UTILITY' });
      toast.success('Sent to Meta for approval. It usually takes minutes to a few hours.');
      loadKapso();
    } catch (err) { toast.error(errMsg(err, 'Could not submit to Meta')); }
    setSubmitting(false);
  };

  const send = async () => {
    if (!body.trim()) return toast.error('Write a message or pick a template');
    if (!recipientCount) return toast.error('No recipients matched');
    if (needsImage && !image.trim()) return toast.error('This template has an image: choose the image to send');
    const how = useTemplate
      ? `as the Meta-approved template "${copy.name}"`
      : 'as typed. It is not a Meta-approved template, so ONLY people who messaged this number in the last 24 hours will get it; the rest will show as failed';
    if (!window.confirm(`Send this WhatsApp message to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''} via Kapso from ${fromText} ${how}?`)) return;
    setSending(true); setResult(null);
    try {
      const res = await kapsoAPI.sendBulk(useTemplate
        ? { phoneNumberId: sendPid, mode: 'template', template: { name: copy.name, language: copy.language }, audience: sendAudience(), ...(needsImage ? { headerImageUrl: image.trim() } : {}) }
        : { phoneNumberId: sendPid, mode: 'text', body, audience: sendAudience() });
      setResult(res.data?.data || null);
      toast.success(res.data?.message || 'Sent');
      loadKapso();
    } catch (err) {
      toast.error(errMsg(err, 'Send failed'));
    } finally { setSending(false); }
  };

  if (sandbox) return <Card title="Send to Customers"><div style={muted}>The sandbox number can't message customers. Pick the real number at the top.</div></Card>;

  const status = !mc && !ko ? null
    : approved
      ? body === baseBody
        ? { tone: 'ok', text: `Approved by Meta. Reaches every recipient, sent as "${copy.name}".` }
        : { tone: 'warn', text: 'You edited the approved wording, so this goes out as typed and only reaches people who messaged in the last 24 hours. Pick the template again to undo.' }
      : copy?.status === 'PENDING'
        ? { tone: 'warn', text: 'Waiting for Meta approval. Until then it only reaches people who messaged in the last 24 hours.' }
        : copy?.status === 'REJECTED'
          ? { tone: 'bad', text: 'Meta rejected this wording. Change it in Message Center (or in Kapso → Templates) and submit again.' }
          : { tone: 'warn', text: 'Not approved by Meta yet, so right now it only reaches people who messaged in the last 24 hours.', submit: true };
  const toneColor = { ok: 'var(--c-166534)', warn: 'var(--c-92400e)', bad: 'var(--danger, #dc2626)' };

  return (
    <div className="card"><div className="card-body">
      <div style={{ background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '.83rem', color: 'var(--c-166534)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <BadgeCheck size={15} />
        Messages go out through the <b>official WhatsApp API (Kapso)</b> from <b>{fromText}</b>.
        {!auto && usage && <span>Today: <b>{usage.used}</b>{usage.limit != null ? ` of ${usage.limit} people messaged, ${left} left.` : ' people messaged.'}</span>}
      </div>

      {roles && (
        <div className="form-group">
          <label className="form-label">Send from</label>
          <select className="form-control" value={from} onChange={e => setFrom(e.target.value)}>
            <option value="auto" disabled={!roles.catv || !roles.broadband}>Auto: CableTV number for CableTV, Broadband number for Broadband{!roles.catv || !roles.broadband ? ' (set both in Overview first)' : ''}</option>
            <option value="selected">The number picked at the top ({number?.display_phone_number})</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Template</label>
        <select className="form-control" value={templateId} onChange={e => applyTemplate(e.target.value)}>
          <option value="">— Write custom message —</option>
          {templates.map(t => (
            <option key={t._id} value={t._id}>
              {t.name} ({CATEGORY_LABELS[t.category] || t.category}{t.serviceType && t.serviceType !== 'both' ? ` · ${SERVICE_TYPE_LABELS[t.serviceType]}` : ''})
            </option>
          ))}
          {kOnly.length > 0 && (
            <optgroup label="Approved on Kapso (no Message Center copy)">
              {kOnly.map(k => <option key={k.id} value={`k:${k.id}`}>{k.name} ({k.language}, {k.category})</option>)}
            </optgroup>
          )}
        </select>
        {status && (
          <div style={{ marginTop: 8, fontSize: '.8rem', color: toneColor[status.tone], display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {status.tone === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            <span>{status.text}</span>
            {status.submit && (
              tooLong ? <span>Too long for Meta ({mc.body.length}/1024 characters). Shorten it in Message Center first.</span>
                : edgeBlank ? <span>Meta won't accept a message that starts or ends with a {'{{blank}}'}. Adjust it in Message Center first.</span>
                  : <button type="button" className="btn btn-secondary btn-sm" onClick={submitToMeta} disabled={submitting}>
                      {submitting ? <Loader size={13} style={spin} /> : <Send size={13} />} Submit to Meta for approval
                    </button>
            )}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Message <span className="required">*</span></label>
        <textarea className="form-control" rows={4} value={body} onChange={e => setBody(e.target.value)}
          placeholder="Dear {{name}}, internet services in {{area}} are temporarily down…" />
        <div className="form-hint">
          Placeholders {PLACEHOLDERS.join(', ')} are filled per customer automatically.
          {!mc && ' A custom message is not Meta-approved, so it only reaches people who messaged in the last 24 hours.'}
        </div>
        {mc && !needsImage && (mc.imageUrl || mc.buttons?.length > 0) && (
          <div className="form-hint" style={{ color: 'var(--c-92400e)' }}>
            This template's {mc.imageUrl ? 'image' : 'buttons'} {mc.imageUrl ? 'is' : 'are'} not sent through Kapso; only the text is.
            {mc.imageUrl && ' To send the image, its Kapso copy needs an image header (Kapso → Templates → New → Header: Image).'}
          </div>
        )}
      </div>

      {needsImage && (
        <div className="form-group">
          <label className="form-label">Image <span className="required">*</span></label>
          <ImagePick value={image} onChange={setImage} />
          <div className="form-hint">Sent above the message to every recipient. JPG or PNG, under 5 MB.</div>
        </div>
      )}

      <RecipientPicker r={rcp} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <RecipientCount r={rcp} />
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={send} disabled={sending || !recipientCount || !body.trim()}>
          {sending
            ? <><Loader size={14} style={{ ...spin, marginRight: 6 }} />Sending… (do not close)</>
            : <><Send size={14} style={{ marginRight: 6 }} />Send WhatsApp to {recipientCount}</>}
        </button>
      </div>
      {!auto && useTemplate && left != null && recipientCount > left && (
        <div style={{ marginTop: 8, fontSize: '.8rem', color: 'var(--c-991b1b)' }}>This send ({recipientCount}) is over the {left} people Meta still allows today.</div>
      )}

      {result && (
        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, background: result.failed ? 'var(--cb-fffbeb)' : 'var(--cb-f0fdf4)', border: `1px solid ${result.failed ? 'var(--cb-fde68a)' : 'var(--cb-bbf7d0)'}`, fontSize: '.85rem' }}>
          <b>Result:</b> {result.sent} sent, {result.failed} failed (of {result.total}). Every message is in Message Center → History.
          {result.errors?.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', color: 'var(--c-92400e)' }}>
              {result.errors.map((e, i) => <li key={i}>{e.mobile}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div></div>
  );
}

// ─── Send: text / template / document / image ───
function SendTab({ pid, waba, number }) {
  const templates = useApprovedTemplates(waba);
  const [kind, setKind] = useState('text');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [tplName, setTplName] = useState('');
  const [params, setParams] = useState({});
  const [url, setUrl] = useState('');
  const [filename, setFilename] = useState('');
  const [sending, setSending] = useState(false);

  const tpl = templates.find(t => `${t.name}|${t.language}` === tplName);
  const { header, body, bodyVars, headerVars } = tplParts(tpl);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = { phoneNumberId: pid, to: to.trim(), kind };
      if (kind === 'text') data.message = message.trim();
      if (kind === 'template') {
        data.template = {
          name: tpl.name, language: tpl.language,
          params: Object.fromEntries(bodyVars.map(v => [v, params[v] || ''])),
          headerParams: Object.fromEntries(headerVars.map(v => [v, params[v] || ''])),
          ...(header?.format === 'DOCUMENT' ? { documentUrl: url.trim(), documentFilename: filename.trim() || undefined } : {}),
          ...(header?.format === 'IMAGE' ? { imageUrl: url.trim() } : {}),
        };
      }
      if (kind === 'document' || kind === 'image') Object.assign(data, { url: url.trim(), filename: filename.trim() || undefined, caption: message.trim() || undefined });
      await kapsoAPI.send(data);
      toast.success('Sent via Kapso');
      setMessage('');
    } catch (err) { toast.error(errMsg(err, 'Send failed')); }
    setSending(false);
  };

  return (
    <Card title={`Send from ${number?.display_name || ''} ${number?.display_phone_number || ''}`}>
      <form onSubmit={send} style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
        <div>
          <label style={label}>Type</label>
          <select className="form-control" value={kind} onChange={e => setKind(e.target.value)}>
            <option value="text">Text (only within 24h of their last message)</option>
            <option value="template">Approved template (any time)</option>
            <option value="document">PDF / document (within 24h)</option>
            <option value="image">Image (within 24h)</option>
          </select>
        </div>
        <div>
          <label style={label}>To (mobile number)</label>
          <input className="form-control" value={to} onChange={e => setTo(e.target.value)} placeholder="9876543210" />
        </div>
        {kind === 'template' && (
          <>
            <div>
              <label style={label}>Template</label>
              <select className="form-control" value={tplName} onChange={e => { setTplName(e.target.value); setParams({}); }}>
                <option value="">{templates.length ? 'Choose…' : 'No approved templates yet (Templates tab)'}</option>
                {templates.map(t => <option key={t.id} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
              </select>
            </div>
            {body && <div style={{ ...muted, whiteSpace: 'pre-wrap', border: '1px dashed var(--border)', borderRadius: 8, padding: 8 }}>{body.text}</div>}
            {[...headerVars, ...bodyVars].map(v => (
              <div key={v}>
                <label style={label}>{v}</label>
                <input className="form-control" value={params[v] || ''} onChange={e => setParams(p => ({ ...p, [v]: e.target.value }))} />
              </div>
            ))}
            {header?.format === 'IMAGE' && (
              <div>
                <label style={label}>Image</label>
                <ImagePick value={url} onChange={setUrl} />
              </div>
            )}
          </>
        )}
        {(kind === 'document' || kind === 'image' || header?.format === 'DOCUMENT') && (
          <>
            <div>
              <label style={label}>Public file link (https://…)</label>
              <input className="form-control" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.laxmicablevision.in/…/receipt.pdf" />
            </div>
            {kind !== 'image' && (
              <div>
                <label style={label}>File name</label>
                <input className="form-control" value={filename} onChange={e => setFilename(e.target.value)} placeholder="receipt.pdf" />
              </div>
            )}
          </>
        )}
        {(kind === 'text' || kind === 'document' || kind === 'image') && (
          <div>
            <label style={label}>{kind === 'text' ? 'Message' : 'Caption (optional)'}</label>
            <textarea className="form-control" rows={3} value={message} onChange={e => setMessage(e.target.value)} />
          </div>
        )}
        <button type="submit" className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }}
          disabled={sending || !to.trim() || (kind === 'text' && !message.trim()) || (kind === 'template' && !tpl)}>
          {sending ? <Loader size={13} style={{ ...spin, marginRight: 5 }} /> : <Send size={13} style={{ marginRight: 5 }} />}Send
        </button>
      </form>
    </Card>
  );
}

// ─── Templates: list + create + delete ───
const EMPTY_TPL = { name: '', category: 'UTILITY', language: 'en', headerType: 'none', headerText: '', headerImageUrl: '', body: '', footer: '', buttons: [], examples: {} };
// Example values Meta's reviewers see for the Message Center placeholders.
const SAMPLE_VALUES = { name: 'Ramesh Patel', area: 'Shivam Society', customer_id: 'LCV1234', username: 'ramesh01', mobile: '9876543210', expiry: '31/10/2026', amount: '300' };
const snake = (s) => s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

// Message Center template → Kapso form: same wording, blanks renamed to Meta's lowercase style
// ({{customerId}} → {{customer_id}}), so "Send to Customers" auto-matches them again.
function fromMessageCenter(t) {
  const body = String(t.body || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => `{{${snake(k)}}}`);
  return {
    ...EMPTY_TPL,
    name: snake(String(t.name || 'template')).replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60),
    category: MARKETING_CATEGORIES.includes(t.category) ? 'MARKETING' : 'UTILITY',
    language: /[઀-૿]/.test(body) ? 'gu' : /[ऀ-ॿ]/.test(body) ? 'hi' : 'en',
    body, // full text: the form shows it in red if it is over Meta's 1024 limit
    ...(t.imageUrl ? { headerType: 'image', headerImageUrl: t.imageUrl } : {}),
    footer: String(t.footer || '').slice(0, 60),
    examples: Object.fromEntries(varsIn(body).map(v => [v, SAMPLE_VALUES[v] || 'Sample'])),
  };
}

function TemplatesTab({ waba }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mcTemplates, setMcTemplates] = useState([]); // Message Center templates to fetch from
  useEffect(() => {
    if (form && !mcTemplates.length) {
      messagingAPI.getTemplates().then(r => setMcTemplates((r.data?.data || []).filter(t => t.channel !== 'sms' && t.isActive !== false))).catch(() => {});
    }
  }, [form, mcTemplates.length]);

  const load = useCallback(async () => {
    if (!waba) return setList([]);
    try { setList((await kapsoAPI.templates(waba)).data.data); }
    catch (err) { toast.error(errMsg(err, 'Could not load templates')); setList([]); }
    return undefined;
  }, [waba]);
  useEffect(() => { load(); }, [load]);

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}" (all languages)? This cannot be undone.`)) return;
    try { await kapsoAPI.deleteTemplate(waba, t.name); toast.success('Deleted'); load(); }
    catch (err) { toast.error(errMsg(err, 'Delete failed')); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { headerType, headerText, headerImageUrl, ...rest } = form;
      await kapsoAPI.createTemplate(waba, {
        ...rest,
        ...(headerType === 'text' ? { headerText } : {}),
        ...(headerType === 'image' ? { headerImageUrl } : {}),
      });
      toast.success('Submitted to Meta for review');
      setForm(null);
      load();
    } catch (err) { toast.error(errMsg(err, 'Submit failed')); }
    setSaving(false);
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const vars = form ? [...new Set([...(form.headerType === 'text' ? varsIn(form.headerText) : []), ...varsIn(form.body)])] : [];

  return (
    <>
      {form && (
        <Card title="New template" right={<button className="btn btn-secondary btn-sm" onClick={() => setForm(null)}><X size={13} /></button>}>
          <form onSubmit={submit} style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
            <div>
              <label style={label}>Fetch from Message Center templates (optional)</label>
              <select className="form-control" value="" onChange={e => { const t = mcTemplates.find(x => x._id === e.target.value); if (t) setForm(fromMessageCenter(t)); }}>
                <option value="">{mcTemplates.length ? '— Pick a current template to fill this form —' : 'Loading Message Center templates…'}</option>
                {mcTemplates.map(t => (
                  <option key={t._id} value={t._id}>
                    {t.name} ({CATEGORY_LABELS[t.category] || t.category}{t.serviceType && t.serviceType !== 'both' ? ` · ${SERVICE_TYPE_LABELS[t.serviceType]}` : ''})
                  </option>
                ))}
              </select>
              <div style={{ ...muted, fontSize: '.72rem', marginTop: 4 }}>
                Fills name, wording, language, category and example values. Edit anything before submitting. Keeping the
                suggested name links it to that template in "Send to Customers".
              </div>
            </div>
            <div className="kp-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
              <div><label style={label}>Name (lowercase_with_underscores)</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} /></div>
              <div><label style={label}>Category</label>
                <select className="form-control" value={form.category} onChange={set('category')}>
                  <option value="UTILITY">Utility (receipts, reminders)</option><option value="MARKETING">Marketing (offers)</option>
                </select></div>
              <div><label style={label}>Language</label>
                <select className="form-control" value={form.language} onChange={set('language')}>
                  <option value="en">English</option><option value="gu">Gujarati</option><option value="hi">Hindi</option>
                </select></div>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={label}>Header (optional)</label>
              <select className="form-control" value={form.headerType} onChange={set('headerType')} style={{ maxWidth: 200 }}>
                <option value="none">None</option><option value="text">Text</option><option value="image">Image</option>
              </select>
              {form.headerType === 'text' && <input className="form-control" value={form.headerText} onChange={set('headerText')} maxLength={60} />}
              {form.headerType === 'image' && (
                <>
                  <ImagePick value={form.headerImageUrl} onChange={v => setForm(f => ({ ...f, headerImageUrl: v }))} />
                  <div style={{ ...muted, fontSize: '.72rem' }}>Sample image for Meta's review. You choose the image again each time you send, so one template works for any picture.</div>
                </>
              )}
            </div>
            <div>
              <label style={label}>Body: use {'{{name}}'}, {'{{amount}}'} etc. for blanks (max 1024 characters)</label>
              <textarea className="form-control" rows={7} value={form.body} onChange={set('body')} />
              <div style={{ ...muted, fontSize: '.72rem', textAlign: 'right', ...(form.body.length > 1024 ? { color: 'var(--danger, #dc2626)', fontWeight: 600 } : {}) }}>
                {form.body.length}/1024{form.body.length > 1024 ? ' — too long for Meta, shorten it before submitting' : ''}
              </div>
            </div>
            <div><label style={label}>Footer (optional, no blanks)</label><input className="form-control" value={form.footer} onChange={set('footer')} maxLength={60} /></div>
            {vars.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={label}>Example value for each blank (Meta reviews these)</div>
                {vars.map(v => (
                  <div key={v} className="kp-grid" style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'center' }}>
                    <code style={{ fontSize: '.8rem' }}>{v}</code>
                    <input className="form-control" value={form.examples[v] || ''} onChange={e => setForm(f => ({ ...f, examples: { ...f.examples, [v]: e.target.value } }))} />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={label}>Buttons (optional, up to 3)</div>
              {form.buttons.map((b, i) => {
                const upd = (k) => (e) => setForm(f => ({ ...f, buttons: f.buttons.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) }));
                return (
                  <div key={i} className="kp-grid" style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr auto', gap: 6 }}>
                    <select className="form-control" value={b.type} onChange={upd('type')}>
                      <option value="QUICK_REPLY">Quick reply</option><option value="URL">Website link</option><option value="PHONE_NUMBER">Call number</option>
                    </select>
                    <input className="form-control" placeholder="Button text" value={b.text} onChange={upd('text')} maxLength={25} />
                    {b.type === 'URL' ? <input className="form-control" placeholder="https://…" value={b.url || ''} onChange={upd('url')} />
                      : b.type === 'PHONE_NUMBER' ? <input className="form-control" placeholder="9327729619" value={b.phone_number || ''} onChange={upd('phone_number')} /> : <span />}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }))}><Trash2 size={13} /></button>
                  </div>
                );
              })}
              {form.buttons.length < 3 && (
                <button type="button" className="btn btn-secondary btn-sm" style={{ justifySelf: 'start' }}
                  onClick={() => setForm(f => ({ ...f, buttons: [...f.buttons, { type: 'QUICK_REPLY', text: '' }] }))}><Plus size={13} /> Add button</button>
              )}
            </div>
            <button className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={saving || !form.name || !form.body.trim() || form.body.length > 1024 || (form.headerType === 'image' && !form.headerImageUrl.trim())}>
              {saving ? <Loader size={13} style={spin} /> : <Send size={13} />} Submit to Meta
            </button>
            <div style={muted}>A PDF header (like a receipt) can't be set up here yet; it needs a file upload to Meta. Ask Claude when you need it.</div>
          </form>
        </Card>
      )}
      <Card title="Templates" right={<>
        {!form && <button className="btn btn-primary btn-sm" onClick={() => setForm(EMPTY_TPL)}><Plus size={13} /> New</button>}
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /></button>
      </>}>
        {list === null ? <Loader size={16} style={spin} /> : !list.length ? <div style={muted}>No templates yet.</div> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {list.map(t => {
              const { header, body } = tplParts(t);
              return (
                <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <b style={{ flex: 1 }}>{t.name}</b>
                    <Pill text={t.category} /><Pill text={t.language} /><Pill text={t.status} tone={statusTone(t.status)} />
                    <button className="btn btn-secondary btn-sm" onClick={() => del(t)} title="Delete"><Trash2 size={13} /></button>
                  </div>
                  {header && <div style={{ ...muted, marginTop: 6 }}>Header: {header.text || header.format}</div>}
                  <div style={{ fontSize: '.82rem', whiteSpace: 'pre-wrap', marginTop: 6 }}>{body?.text}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Broadcasts: create draft → review → send ───
function BroadcastsTab({ pid, waba }) {
  const templates = useApprovedTemplates(waba);
  const [list, setList] = useState(null);
  const [name, setName] = useState('');
  const [tplId, setTplId] = useState('');
  const [lines, setLines] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setList((await kapsoAPI.broadcasts()).data.data); }
    catch (err) { toast.error(errMsg(err, 'Could not load broadcasts')); setList([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tpl = templates.find(t => t.id === tplId);
  const { bodyVars } = tplParts(tpl);
  // Each line: mobile[, value for blank 1, value for blank 2, …]
  const recipients = lines.split('\n').map(l => l.split(',').map(s => s.trim())).filter(p => p[0])
    .map(([phone, ...vals]) => ({ phone, params: Object.fromEntries(bodyVars.map((v, i) => [v, vals[i] || ''])) }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await kapsoAPI.createBroadcast({ name, phoneNumberId: pid, templateId: tplId, recipients });
      const d = res.data.data;
      toast.success(`Draft saved with ${d.added} recipients${d.errors?.length ? ` (${d.errors.length} errors)` : ''}. Press Send in the list to send it.`);
      setName(''); setLines('');
      load();
    } catch (err) { toast.error(errMsg(err, 'Could not create broadcast')); }
    setBusy(false);
  };
  const send = async (b) => {
    if (!window.confirm(`Send "${b.name}" to ${b.total_recipients} people now? Real WhatsApp messages, charged by Meta.`)) return;
    try { await kapsoAPI.sendBroadcast(b.id); toast.success('Sending started'); load(); }
    catch (err) { toast.error(errMsg(err, 'Send failed')); }
  };

  return (
    <>
      <Card title="New broadcast (saved as a draft first)">
        <form onSubmit={create} style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
          <div><label style={label}>Name</label><input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="Diwali offer 2026" /></div>
          <div>
            <label style={label}>Template</label>
            <select className="form-control" value={tplId} onChange={e => setTplId(e.target.value)}>
              <option value="">{templates.length ? 'Choose…' : 'No approved templates yet'}</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.language}, {t.category})</option>)}
            </select>
          </div>
          <div>
            <label style={label}>
              Recipients, one per line: mobile{bodyVars.length ? `, ${bodyVars.join(', ')}` : ''}
            </label>
            <textarea className="form-control" rows={6} value={lines} onChange={e => setLines(e.target.value)}
              placeholder={bodyVars.length ? `9876543210, ${bodyVars.map(v => `<${v}>`).join(', ')}` : '9876543210'} />
            <div style={{ ...muted, fontSize: '.72rem' }}>{recipients.length} recipients</div>
          </div>
          <button className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={busy || !name || !tplId || !recipients.length}>
            {busy ? <Loader size={13} style={spin} /> : <Plus size={13} />} Save draft
          </button>
        </form>
      </Card>
      <Card title="Broadcasts" right={<button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /></button>}>
        {list === null ? <Loader size={16} style={spin} /> : !list.length ? <div style={muted}>No broadcasts yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: '.82rem' }}>
              <thead><tr><th>Name</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Delivered</th><th>Read</th><th>Failed</th><th>Replied</th><th /></tr></thead>
              <tbody>
                {list.map(b => (
                  <tr key={b.id}>
                    <td>{b.name}<div style={muted}>{fmtTs(b.created_at)}</div></td>
                    <td><Pill text={b.status} tone={statusTone(b.status)} /></td>
                    <td>{b.total_recipients ?? '—'}</td><td>{b.sent_count ?? '—'}</td><td>{b.delivered_count ?? '—'}</td>
                    <td>{b.read_count ?? '—'}</td><td>{b.failed_count ?? '—'}</td><td>{b.responded_count ?? '—'}</td>
                    <td>{b.status === 'draft' && <button className="btn btn-primary btn-sm" onClick={() => send(b)}><Send size={13} /> Send</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Contacts ───
function ContactsTab() {
  const [list, setList] = useState(null);
  const load = useCallback(async () => {
    try { setList((await kapsoAPI.contacts()).data.data); }
    catch (err) { toast.error(errMsg(err, 'Could not load contacts')); setList([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card title="Contacts (people who chatted with the number)" right={<button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /></button>}>
      {list === null ? <Loader size={16} style={spin} /> : !list.length ? <div style={muted}>No contacts yet.</div> : (
        <table className="table" style={{ width: '100%', fontSize: '.82rem' }}>
          <thead><tr><th>Name</th><th>Number</th><th>First seen</th></tr></thead>
          <tbody>{list.map(c => <tr key={c.id}><td>{c.display_name || c.profile_name || c.name || '—'}</td><td>{c.wa_id || c.phone_number}</td><td>{fmtTs(c.created_at)}</td></tr>)}</tbody>
        </table>
      )}
    </Card>
  );
}

// ─── Business profile + blocked users ───
const VERTICALS = ['OTHER', 'PROF_SERVICES', 'RETAIL', 'ENTERTAIN', 'EDU', 'FINANCE', 'GOVT', 'HEALTH', 'TRAVEL'];
function ProfileTab({ pid, number }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [blockNo, setBlockNo] = useState('');
  const sandbox = number?.kind === 'sandbox';

  const loadBlocked = useCallback(() => kapsoAPI.blocked(pid).then(r => setBlocked(r.data.data)).catch(() => setBlocked([])), [pid]);
  useEffect(() => {
    if (!pid || sandbox) return;
    kapsoAPI.profile(pid).then(r => setP({ ...r.data.data, websites: r.data.data.websites || [] }))
      .catch(err => { toast.error(errMsg(err, 'Could not load profile')); setP({ websites: [] }); });
    loadBlocked();
  }, [pid, sandbox, loadBlocked]);

  if (sandbox) return <Card title="Business profile"><div style={muted}>The sandbox number has no business profile. Pick the real number at the top.</div></Card>;
  const set = (k) => (e) => setP(x => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await kapsoAPI.saveProfile(pid, p); toast.success('Profile saved'); }
    catch (err) { toast.error(errMsg(err, 'Save failed')); }
    setSaving(false);
  };
  const toggleBlock = async (phone, block) => {
    try { await kapsoAPI.setBlocked(pid, phone, block); toast.success(block ? 'Blocked' : 'Unblocked'); setBlockNo(''); loadBlocked(); }
    catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  return (
    <>
      <Card title="Business profile (what customers see when they tap your name)">
        {!p ? <Loader size={16} style={spin} /> : (
          <form onSubmit={save} style={{ display: 'grid', gap: 12, maxWidth: 520 }}>
            <div><label style={label}>About (max 139)</label><input className="form-control" value={p.about || ''} onChange={set('about')} maxLength={139} /></div>
            <div><label style={label}>Description (max 512)</label><textarea className="form-control" rows={3} value={p.description || ''} onChange={set('description')} maxLength={512} /></div>
            <div><label style={label}>Address</label><input className="form-control" value={p.address || ''} onChange={set('address')} maxLength={256} /></div>
            <div><label style={label}>Email</label><input className="form-control" type="email" value={p.email || ''} onChange={set('email')} /></div>
            {[0, 1].map(i => (
              <div key={i}><label style={label}>Website {i + 1}</label>
                <input className="form-control" value={p.websites[i] || ''} placeholder="https://www.laxmicablevision.in"
                  onChange={e => setP(x => { const w = [...x.websites]; w[i] = e.target.value; return { ...x, websites: w }; })} /></div>
            ))}
            <div><label style={label}>Business type</label>
              <select className="form-control" value={p.vertical || 'OTHER'} onChange={set('vertical')}>{VERTICALS.map(v => <option key={v}>{v}</option>)}</select></div>
            <button className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={saving}>{saving ? <Loader size={13} style={spin} /> : <CheckCircle2 size={13} />} Save</button>
          </form>
        )}
      </Card>
      <Card title="Blocked numbers">
        <div style={{ display: 'flex', gap: 8, maxWidth: 420, marginBottom: 12 }}>
          <input className="form-control" value={blockNo} onChange={e => setBlockNo(e.target.value)} placeholder="Mobile number to block" />
          <button className="btn btn-secondary btn-sm" disabled={!blockNo.trim()} onClick={() => toggleBlock(blockNo.trim(), true)}><ShieldBan size={13} /> Block</button>
        </div>
        {!blocked.length ? <div style={muted}>Nobody is blocked.</div> : blocked.map(b => (
          <div key={b.wa_id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '.85rem', marginBottom: 6 }}>
            <span style={{ flex: 1 }}>{b.wa_id}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => toggleBlock(b.wa_id, false)}>Unblock</button>
          </div>
        ))}
      </Card>
    </>
  );
}

// ─── Everything Kapso can do, and where it lives ───
const FEATURES = [
  ['Send text, PDF and image messages', 'here', 'Send tab. Free text only within 24h of the customer\'s last message.'],
  ['Approved templates (receipts, reminders, offers)', 'here', 'Templates tab: create, check Meta approval, delete.'],
  ['Inbox with replies', 'here', 'Inbox tab. Reply box locks after 24h; use a template then.'],
  ['Broadcasts with delivered / read / replied counts', 'here', 'Broadcasts tab. Saved as draft, sent only when you press Send.'],
  ['Number health, quality and daily limit', 'here', 'Overview tab.'],
  ['Business profile (about, address, website)', 'here', 'Profile & Blocking tab.'],
  ['Block / unblock numbers', 'here', 'Profile & Blocking tab.'],
  ['Contacts list', 'here', 'Contacts tab.'],
  ['Automatic receipts / reminders from the CRM', 'next', 'Planned: receipt template with PDF, Baileys as fallback.'],
  ['Delivery status + replies saved in CRM (webhook)', 'next', 'Planned: messages land in Message Log and WhatsApp Inbox.'],
  ['Template with PDF header (receipt attached)', 'next', 'Needs a one-time media upload to Meta.'],
  ['Buttons and list menus in replies', 'later', 'Interactive messages, e.g. "Pay now" / "Raise complaint".'],
  ['WhatsApp Flows (forms inside WhatsApp)', 'dashboard', 'e.g. new-connection or complaint form. Built in the Kapso dashboard.'],
  ['Workflows / auto-replies', 'dashboard', 'Visual bot builder in the Kapso dashboard.'],
  ['AI agent that answers customers', 'dashboard', 'Kapso agents. Would need CRM data access to be useful.'],
  ['WhatsApp calling', 'dashboard', 'Business calling, off on this number.'],
  ['Username (@laxmicablevision)', 'dashboard', 'Claim a WhatsApp username.'],
  ['Display name change, conversation assignment, API logs, alerts', 'dashboard', 'Kapso dashboard.'],
];
function FeaturesTab() {
  const tone = { here: 'ok', next: 'warn', later: 'muted', dashboard: 'muted' };
  const text = { here: 'On this page', next: 'Next step', later: 'Possible later', dashboard: 'Kapso dashboard' };
  return (
    <Card title="What Kapso can do" right={<a className="btn btn-secondary btn-sm" href="https://app.kapso.ai" target="_blank" rel="noreferrer">Open Kapso dashboard</a>}>
      <div style={{ display: 'grid', gap: 6 }}>
        {FEATURES.map(([name, where, note]) => (
          <div key={name} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div><div style={{ fontWeight: 600, fontSize: '.85rem' }}>{name}</div><div style={muted}>{note}</div></div>
            <div><Pill text={text[where]} tone={tone[where]} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}
